import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import tiffinService from '../services/tiffinService';

const TiffinSelection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const centerId = searchParams.get('centerId');

  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (centerId) {
      fetchPlans();
    }
  }, [centerId]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const data = await tiffinService.getPlansByCenter(centerId);
      setPlans(data || []);
      setError(null);
    } catch (err) {
      setError('Failed to load plans. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
  };

  const handleProceedToSubscription = () => {
    if (selectedPlan) {
      navigate(`/tiffin/registration?planId=${selectedPlan.planId}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container-custom">
        <div className="mb-12">
          <button
            onClick={() => navigate('/tiffin/center')}
            className="text-blue-600 hover:text-blue-800 mb-4 font-semibold"
          >
            ← Back to Centers
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Select Meal Plan</h1>
          <p className="text-lg text-gray-600">
            Choose a plan to subscribe
          </p>
        </div>

        {loading && (
          <div className="text-center py-12">
            <div className="inline-block">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
            <p className="mt-4 text-gray-600">Loading plans...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {!loading && plans.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="space-y-4">
                {plans.map((plan) => (
                  <div
                    key={plan.planId}
                    onClick={() => handleSelectPlan(plan)}
                    className={`item-card cursor-pointer ${
                      selectedPlan?.planId === plan.planId
                        ? 'selected'
                        : ''
                    }`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">
                          {plan.planName}
                        </h3>
                        <p className="text-gray-600 text-sm mt-1">
                          Cuisine: {plan.cuisineType}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-blue-600">
                          ${plan.totalPrice}
                        </p>
                        <p className="text-gray-600 text-sm">
                          ${plan.pricePerMeal}/meal
                        </p>
                      </div>
                    </div>
                    <p className="text-gray-700 mb-4">
                      {plan.planDescription}
                    </p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Plan Type</p>
                        <p className="font-semibold capitalize">{plan.planType}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Meals</p>
                        <p className="font-semibold">{plan.mealCount}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Delivery Days</p>
                        <p className="font-semibold">{plan.deliveryDays}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Price/Meal</p>
                        <p className="font-semibold">${plan.pricePerMeal}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              {selectedPlan ? (
                <div className="card sticky top-4">
                  <h3 className="text-xl font-bold mb-4">Plan Summary</h3>
                  <div className="space-y-4 mb-6">
                    <div>
                      <p className="text-gray-600 text-sm">Plan Name</p>
                      <p className="font-semibold">{selectedPlan.planName}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 text-sm">Cuisine Type</p>
                      <p className="font-semibold capitalize">{selectedPlan.cuisineType}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 text-sm">Total Meals</p>
                      <p className="font-semibold">{selectedPlan.mealCount}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 text-sm">Plan Type</p>
                      <p className="font-semibold capitalize">{selectedPlan.planType}</p>
                    </div>
                    <div className="border-t pt-4">
                      <p className="text-gray-600 text-sm">Total Price</p>
                      <p className="text-3xl font-bold text-blue-600">
                        ${selectedPlan.totalPrice}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleProceedToSubscription}
                    className="btn-primary w-full"
                  >
                    Subscribe Now
                  </button>
                </div>
              ) : (
                <div className="card text-center text-gray-500">
                  <p>Select a plan to view details</p>
                </div>
              )}
            </div>
          </div>
        )}

        {!loading && plans.length === 0 && !error && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No plans available for this center.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TiffinSelection;
